/**
 * CampaignActionCard - Card Studio de ação pós-disparo (Etapa 6)
 *
 * Replica fielmente o DNA visual do Studio da Etapa 4 (MessageGroupManager):
 *   - Container `rounded-2xl bg-white border shadow-lg shadow-black/5`
 *   - Header cockpit branco com badge `#01` mono indigo + dot pulsante + nome + pill ↑↓⧉🗑
 *   - Conteúdo expandido em `bg-zinc-50/50` (accordion controlado pelo pai)
 *
 * Diferença vs Etapa 4: como cada ação tem 1 valor (tag/fase/depto/agente/fila)
 * e 1 condição (todos/sucesso/erro), o corpo expandido mostra apenas 2 colunas:
 *   [ Select de valor ] [ Segmented control de condição ]
 *
 * @author Sistema de Campanhas v4.0 (Studio Layout)
 */

import { useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  Check,
  ChevronDown,
  ChevronUp,
  Copy,
  Trash2,
} from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  CampaignActionItem,
  ExecutionCondition,
  campaignActionConfig,
  executionConditionConfig,
} from './campaign-actions-types';
import { CrmUserFilter } from '@/components/crm/filters/CrmUserFilter';


// ============================================================================
// TIPOS
// ============================================================================

/**
 * Opção genérica para os selects (tag, fase, departamento, agente, fila).
 * Campos opcionais:
 *   - color: cor associada (tags e fases) → exibe bolinha no item e header
 *   - groupId/groupName: quando presentes, força renderização agrupada via
 *     Popover+Command (usado para fases agrupadas por categoria do kanban)
 */
export interface ActionValueOption {
  id: string;
  name: string;
  color?: string | null;
  groupId?: string;
  groupName?: string;
}

interface CampaignActionCardProps {
  item: CampaignActionItem;
  index: number;
  total: number;
  isActive: boolean;
  resolvedName?: string;
  /** Lista de opções carregadas para o tipo desta ação */
  options: ActionValueOption[];
  onToggleActive: () => void;
  onChangeValue: (newValue: string) => void;
  onChangeCondition: (cond: ExecutionCondition) => void;
  onMoveUp: () => void;
  onMoveDown: () => void;
  onDuplicate: () => void;
  onRemove: () => void;
}

// ============================================================================
// COMPONENTE PRINCIPAL
// ============================================================================

export function CampaignActionCard({
  item,
  index,
  total,
  isActive,
  resolvedName,
  options,
  onToggleActive,
  onChangeValue,
  onChangeCondition,
  onMoveUp,
  onMoveDown,
  onDuplicate,
  onRemove,
}: CampaignActionCardProps) {
  // Configuração visual do tipo de ação (ícone, label, valueKey)
  const actionConfig = campaignActionConfig[item.action_type];
  const conditionConfig = executionConditionConfig[item.execution_condition];

  // Valor atual selecionado (id) e flag de preenchimento (dot status)
  const valueKey = actionConfig.valueKey;
  const currentValueId = item.action_value?.[valueKey] || '';
  const hasValue = Boolean(currentValueId);

  // Opção selecionada (para exibir bolinha de cor no header e trigger)
  const selectedOption = useMemo(
    () => options.find((o) => o.id === currentValueId),
    [options, currentValueId]
  );

  // Verifica se há agrupamento (qualquer opção com groupId)
  const isGrouped = useMemo(
    () => options.some((o) => o.groupId),
    [options]
  );

  // Estado de abertura do popover (somente quando agrupado)
  const [popoverOpen, setPopoverOpen] = useState(false);

  // Label numérico zero-padded (#01, #02, ...)
  const indexLabel = String(index + 1).padStart(2, '0');

  // Lista de condições para o segmented control
  const conditions: ExecutionCondition[] = ['all', 'success', 'error'];

  return (
    <div
      className={cn(
        'rounded-2xl overflow-hidden transition-all bg-white border shadow-lg shadow-black/5',
        isActive
          ? 'border-indigo-500/40 ring-1 ring-indigo-500/20'
          : 'border-zinc-200 hover:border-zinc-300'
      )}
    >
      {/* ====== Header cockpit (clicável → toggle accordion) ====== */}
      <div
        className="flex flex-wrap items-center gap-4 px-5 py-3.5 bg-white border-b border-zinc-200 cursor-pointer"
        onClick={onToggleActive}
      >
        {/* Bloco de identidade: badge #, dot, ícone+label, valor resolvido */}
        <div className="flex items-center gap-3 min-w-0 flex-1">
          {/* Badge índice mono indigo */}
          <span className="font-mono text-[11px] text-indigo-500 bg-indigo-500/10 px-2 py-1 rounded-md border border-indigo-500/20 shrink-0">
            #{indexLabel}
          </span>

          {/* Dot status pulsante (preenchido vs vazio) — espelha Etapa 4 */}
          <div className="relative flex items-center shrink-0" aria-hidden="true">
            <div
              className={cn(
                'w-2 h-2 rounded-full',
                hasValue ? 'bg-emerald-500 animate-pulse' : 'bg-zinc-300'
              )}
            />
            {hasValue && (
              <div className="absolute w-2 h-2 bg-emerald-500 rounded-full opacity-50 blur-sm" />
            )}
          </div>

          {/* Ícone + Label do tipo de ação */}
          <div className="flex items-center gap-2 min-w-0">
            <div className={cn('p-1.5 rounded-md shrink-0', actionConfig.bgColor)}>
              <actionConfig.icon className={cn('h-3.5 w-3.5', actionConfig.iconColor)} />
            </div>
            <span className="text-sm font-semibold text-zinc-900 truncate">
              {actionConfig.label}
            </span>
            {resolvedName && (
              <span className="text-sm text-zinc-500 truncate hidden sm:inline flex items-center gap-1.5">
                ·{' '}
                {selectedOption?.color && (
                  <span
                    className="inline-block w-2 h-2 rounded-full shrink-0"
                    style={{ backgroundColor: selectedOption.color }}
                    aria-hidden="true"
                  />
                )}
                {resolvedName}
              </span>
            )}
          </div>
        </div>

        {/* Badge de condição atual (replicando o contador "X itens" da Etapa 4) */}
        <span
          className={cn(
            'text-[10px] uppercase tracking-wider font-bold px-2 py-0.5 rounded shrink-0',
            conditionConfig.color
          )}
        >
          Se {conditionConfig.label}
        </span>

        {/* Pill de ações ↑ ↓ ⧉ 🗑 — idêntico ao MessageGroupManager */}
        <div
          className="flex items-center bg-zinc-100 p-1 rounded-lg border border-zinc-200 shrink-0"
          onClick={(e) => e.stopPropagation()}
        >
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200"
            onClick={onMoveUp}
            disabled={index === 0}
          >
            <ChevronUp className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200"
            onClick={onMoveDown}
            disabled={index === total - 1}
          >
            <ChevronDown className="h-4 w-4" />
          </Button>
          <div className="w-px h-4 bg-zinc-300 mx-1" />
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-zinc-500 hover:text-zinc-900 hover:bg-zinc-200"
            onClick={onDuplicate}
          >
            <Copy className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="h-7 w-7 text-rose-500 hover:text-rose-600 hover:bg-rose-50"
            onClick={onRemove}
          >
            <Trash2 className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* ====== Corpo expandido (somente quando ativo) ====== */}
      {isActive && (
        <div className="bg-zinc-50/50 p-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {/* Coluna 1 — Select do valor */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                {actionConfig.label.replace(/^(Adicionar|Definir|Atribuir|Fila de) /, '')}
              </label>
              {valueKey === 'agent_id' ? (
                // Renderer dedicado para "Atribuir agente": reusa o mesmo
                // CrmUserFilter dos filtros rápidos de Negócios/Tarefas
                // (avatar + busca + cache compartilhado via useTenantMembers).
                <CrmUserFilter
                  value={currentValueId || null}
                  onChange={(id) => onChangeValue(id ?? '')}
                  label="Selecione um agente..."
                  searchPlaceholder="Buscar agente..."
                  allLabel="Nenhum (limpar)"
                  groupLabel="Agentes"
                  className="w-full justify-between h-10 bg-white border-zinc-200 hover:bg-white"
                />
              ) : isGrouped ? (
                // Renderer agrupado: Popover + Command (estilo CrmDealPhaseFilter)
                // Usado quando as opções vêm agrupadas (ex.: fases por categoria do kanban)
                <Popover open={popoverOpen} onOpenChange={setPopoverOpen}>
                  <PopoverTrigger asChild>
                    <Button
                      type="button"
                      variant="outline"
                      role="combobox"
                      aria-expanded={popoverOpen}
                      className="h-10 w-full justify-between bg-white border-zinc-200 font-normal hover:bg-white"
                    >
                      {selectedOption ? (
                        <span className="flex items-center gap-2 min-w-0">
                          <span
                            className="w-2.5 h-2.5 rounded-full shrink-0"
                            style={{ backgroundColor: selectedOption.color || '#94a3b8' }}
                            aria-hidden="true"
                          />
                          <span className="truncate">{selectedOption.name}</span>
                          {selectedOption.groupName && (
                            <span className="text-xs text-zinc-400 truncate">
                              · {selectedOption.groupName}
                            </span>
                          )}
                        </span>
                      ) : (
                        <span className="text-muted-foreground">Selecione uma opção...</span>
                      )}
                      <ChevronDown className="h-4 w-4 opacity-50 shrink-0" />
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent
                    className="p-0 w-[--radix-popover-trigger-width] min-w-[260px]"
                    align="start"
                  >
                    <Command>
                      <CommandInput placeholder="Buscar..." />
                      <CommandList>
                        <CommandEmpty>Nenhuma opção encontrada.</CommandEmpty>
                        {(() => {
                          // Agrupa opções por groupName preservando ordem
                          const groups = new Map<string, ActionValueOption[]>();
                          options.forEach((opt) => {
                            const key = opt.groupName || 'Sem categoria';
                            if (!groups.has(key)) groups.set(key, []);
                            groups.get(key)!.push(opt);
                          });
                          return Array.from(groups.entries()).map(([groupName, opts]) => (
                            <CommandGroup key={groupName} heading={groupName}>
                              {opts.map((opt) => {
                                const isSelected = opt.id === currentValueId;
                                return (
                                  <CommandItem
                                    key={opt.id}
                                    value={`${groupName} ${opt.name}`}
                                    onSelect={() => {
                                      onChangeValue(opt.id);
                                      setPopoverOpen(false);
                                    }}
                                    className="cursor-pointer"
                                  >
                                    <Check
                                      className={cn(
                                        'mr-2 h-3.5 w-3.5',
                                        isSelected ? 'opacity-100 text-indigo-500' : 'opacity-0'
                                      )}
                                    />
                                    <span
                                      className="w-2.5 h-2.5 rounded-full mr-2 shrink-0"
                                      style={{ backgroundColor: opt.color || '#94a3b8' }}
                                      aria-hidden="true"
                                    />
                                    <span className="truncate">{opt.name}</span>
                                  </CommandItem>
                                );
                              })}
                            </CommandGroup>
                          ));
                        })()}
                      </CommandList>
                    </Command>
                  </PopoverContent>
                </Popover>
              ) : (
                // Renderer padrão: Select com bolinha de cor opcional (tags)
                <Select
                  value={currentValueId || undefined}
                  onValueChange={onChangeValue}
                >
                  <SelectTrigger className="h-10 bg-white border-zinc-200 focus:ring-indigo-500/30 focus:border-indigo-500">
                    <SelectValue placeholder="Selecione uma opção..." />
                  </SelectTrigger>
                  <SelectContent>
                    {options.length === 0 ? (
                      <div className="px-3 py-2 text-xs text-muted-foreground">
                        Nenhuma opção disponível
                      </div>
                    ) : (
                      options.map((opt) => (
                        <SelectItem key={opt.id} value={opt.id}>
                          <span className="flex items-center gap-2">
                            {opt.color && (
                              <span
                                className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                                style={{ backgroundColor: opt.color }}
                                aria-hidden="true"
                              />
                            )}
                            <span className="truncate">{opt.name}</span>
                          </span>
                        </SelectItem>
                      ))
                    )}
                  </SelectContent>
                </Select>
              )}
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                {actionConfig.description}
              </p>
            </div>

            {/* Coluna 2 — Segmented control de condição */}
            <div className="space-y-2">
              <label className="block text-[10px] font-bold text-zinc-500 uppercase tracking-widest">
                Condição de execução
              </label>
              <div className="flex items-center bg-zinc-100 p-1 rounded-lg border border-zinc-200">
                {conditions.map((cond) => {
                  const cfg = executionConditionConfig[cond];
                  const selected = item.execution_condition === cond;
                  return (
                    <button
                      key={cond}
                      type="button"
                      onClick={() => onChangeCondition(cond)}
                      className={cn(
                        'flex-1 flex items-center justify-center gap-1.5 h-8 rounded-md text-xs font-semibold transition-all',
                        selected
                          ? 'bg-white shadow-sm text-zinc-900'
                          : 'text-zinc-500 hover:text-zinc-700'
                      )}
                    >
                      <cfg.icon className="h-3.5 w-3.5" />
                      {cfg.label}
                    </button>
                  );
                })}
              </div>
              <p className="text-[11px] text-zinc-500 leading-relaxed">
                {executionConditionConfig[item.execution_condition].description}
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
